import {
  Box,
  Grid,
  Button,
  Typography,
  TextField,
  MenuItem,
  Slider,
  Card,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { forwardRef } from "react";
import { useImperativeHandle } from "react";
import { useState, useEffect } from "react";

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

function StudySelectionForm(props, ref) {
  const [intakes, setIntakes] = useState([]);
  const [selectedStudyLevel, setSelectedStudyLevel] = useState(null);
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [selectedYear, setSelectedYear] = useState("");
  const [workExperience, setWorkExperience] = useState(0);
  const [errors, setErrors] = useState({});

  // Map labels to local image files
  const imageMap = {
    Winter: "SAPImages/intake/winter.png",
    Spring: "SAPImages/intake/spring.png",
    Summer: "SAPImages/intake/summer.png",
    Autumn: "SAPImages/intake/autumn.png",
    Bachelors: "SAPImages/academic/degree.png",
    Masters: "SAPImages/academic/masters.png",
    Public: "SAPImages/academic/public.png",
    Private: "SAPImages/academic/private.png",
  };

  const studyLevels = [
    { id: 1, name: "Bachelors" },
    { id: 2, name: "Masters" },
  ];

  // Generate current year + next 5 years
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear + i);

  useEffect(() => {
    fetch(`${API_ENDPOINT}intakes`)
      .then((response) => response.json())
      .then((data) => {
        setIntakes(data?.data);
      })
      .catch((error) => {
        console.error("Error fetching tests:", error);
      });
  }, []);

  const validate = () => {
    let newErrors = {};

    if (!selectedStudyLevel) {
      newErrors.studyLevel = "Please select a study level.";
    }
    if (!selectedIntake) {
      newErrors.intake = "Please select a preferred intake.";
    }
    if (!selectedYear) {
      newErrors.year = "Please select intake year.";
    }
    if (selectedStudyLevel === 2 && workExperience === null) {
      newErrors.workExperience = "Please select your work experience.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getData = () => ({
    prospect_study_level: selectedStudyLevel,
    intake_id: selectedIntake,
    intake_year: selectedYear,
    work_experience: selectedStudyLevel === 2 ? workExperience : null,
  });

  useImperativeHandle(ref, () => ({ validate, getData }));

  return (
    <Box
      className="scrollbar-hide"
      sx={{
        //  Add Scrollbar
        maxHeight: "400px",
        overflowY: "auto",

        /* Optional: smooth scrollbar look */
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#D9D9D9",
          borderRadius: "8px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#F5F5F5",
        },
      }}
    >
      {/* Study level */}
      <Typography
        sx={{
          fontFamily: "Inter",
          fontWeight: 500,
          fontStyle: "normal",
          fontSize: "medium",
          lineHeight: "100%",
          letterSpacing: "0px",
          verticalAlign: "middle",
          mb: 3,
          color: "#111111",
        }}
      >
        What do you wish to pursue?
      </Typography>

      {errors.studyLevel && (
        <Typography color="error">{errors.studyLevel}</Typography>
      )}

      <Grid container spacing={2}>
        {studyLevels.map((level) => (
          <Grid item xs={12} sm={6} md={4} key={level.id}>
            <Button
              fullWidth
              onClick={() => setSelectedStudyLevel(level.id)}
              sx={{
                borderRadius: "8px",
                boxShadow: "0px 0px 10px 0px #0000001A",
                color: selectedStudyLevel === level.id ? "#572575" : "#000",
                backgroundColor:
                  selectedStudyLevel === level.id ? "#F9EEFF" : "#fff",
                "&:hover": {
                  backgroundColor:
                    selectedStudyLevel === level.id ? "#F9EEFF" : "#f5f5f5",
                },
                border:
                  selectedStudyLevel === level.id
                    ? "2px solid #572575"
                    : "none",
                textTransform: "none",
                fontFamily: "inter",
                fontSize: "16px",
                fontWeight: selectedStudyLevel === level.id ? 600 : 400,
                p: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              <img
                src={imageMap[level.name] || "SAPImages/academic/10th.png"}
                alt={level.name}
                style={{ width: "40px", height: "40px", objectFit: "contain" }}
              />
              {level.name}
            </Button>
          </Grid>
        ))}
      </Grid>

      {selectedStudyLevel === 2 && (
        <Box mt={4}>
          <Typography
            sx={{
              fontFamily: "Inter",
              fontWeight: 500,
              fontStyle: "normal",
              fontSize: "medium",
              lineHeight: "100%",
              letterSpacing: "0px",
              verticalAlign: "middle",
              mb: 1,
              color: "#111111",
            }}
          >
            Select Your Work Experience ?
          </Typography>

          <Card
            sx={{
              p: 3,
              borderRadius: "16px",
              boxShadow: "0px 4px 15px rgba(0,0,0,0.1)",
            }}
          >
            {/* --- Top Row: Label + Badge --- */}
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontStyle: "normal",
                  fontSize: "medium",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  verticalAlign: "middle",
                  mb: 1,
                  color: "#111111",
                }}
              >
                Select Year
              </Typography>

              <Box
                sx={{
                  border: "1px solid #E2A109",
                  background: "#ffff",
                  color: "#E2A109",
                  px: 2,
                  py: "4px",
                  fontFamily: "Inter",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                {workExperience.toString().padStart(2, "0")} Years
              </Box>
            </Box>

            {/* --- Slider --- */}
            <Slider
              value={workExperience}
              onChange={(e, newVal) => setWorkExperience(newVal)}
              min={0}
              max={5}
              step={1}
              marks={[
                { value: 0, label: "0" },
                { value: 1, label: "1" },
                { value: 2, label: "2" },
                { value: 3, label: "3" },
                { value: 4, label: "4" },
                { value: 5, label: "5" },
              ]}
              sx={{
                height: 15,
                color: "#F4C464",
                padding: "10px 0",

                /* track (filled part) */
                "& .MuiSlider-track": {
                  height: 10,
                  borderRadius: "10px",
                },

                /* rail (unfilled part) */
                "& .MuiSlider-rail": {
                  height: 10,
                  opacity: 1,
                  backgroundColor: "#FFE3B3",
                  borderRadius: "10px",
                },

                /* thumb */
                "& .MuiSlider-thumb": {
                  width: 22,
                  height: 22,
                  backgroundColor: "#F4C464",
                  border: "4px solid #FEE7C1",
                  boxShadow: "0 0 4px rgba(0,0,0,0.15)",
                  "&:focus, &:hover, &.Mui-active": {
                    boxShadow: "0 0 0 8px rgba(255,212,130,0.3)",
                  },
                },

                /* marks (ticks) */
                "& .MuiSlider-mark": {
                  backgroundColor: "#E2A109",
                  width: 0,
                  height: 0,
                  borderRadius: "50%",
                  opacity: 0.6,
                  marginTop: "1px",
                },

                /* mark labels */
                "& .MuiSlider-markLabel": {
                  color: "#C58B0F",
                  fontFamily: "Inter",
                  fontSize: "14px",
                  marginTop: "6px",
                },
              }}
            />

            {errors.workExperience && (
              <Typography color="error" sx={{ mt: 1 }}>
                {errors.workExperience}
              </Typography>
            )}
          </Card>
        </Box>
      )}

      {/* Intake + Year selection */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={4}
        mb={1}
      >
        <Typography
          sx={{
            fontFamily: "Inter",
            fontWeight: 500,
            fontStyle: "normal",
            fontSize: "medium",
            lineHeight: "100%",
            letterSpacing: "0px",
            verticalAlign: "middle",
            mb: 3,
            color: "#111111",
          }}
        >
          Preferred Intake?
        </Typography>
        {errors.intake && (
          <Typography color="error">{errors.intake}</Typography>
        )}

        <FormControl fullWidth sx={{ mb: 3, maxWidth: 220 }}>
          <Select
            displayEmpty
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            sx={{
              height: 40,
              borderRadius: "12px",
              backgroundColor: "#ffffff",
              fontFamily: "Inter",
              fontSize: "14px",
              color: "#000000",
            }}
            renderValue={(value) =>
              value ? (
                value
              ) : (
                <span style={{ color: "#9E9E9E" }}>Select Intake Year</span>
              )
            }
          >
            {Array.from({ length: 6 }, (_, i) => {
              const year = new Date().getFullYear() + i;
              return (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {errors.year && (
          <Typography color="error" sx={{ fontSize: "13px", mt: 0.5 }}>
            {errors.year}
          </Typography>
        )}
      </Box>

      <Grid container spacing={2}>
        {intakes.map((test) => (
          <Grid item xs={12} sm={6} md={4} key={test.id}>
            <Button
              fullWidth
              onClick={() => setSelectedIntake(test.id)}
              sx={{
                borderRadius: "8px",
                boxShadow: "0px 0px 10px 0px #0000001A",
                color: selectedIntake === test.id ? "#572575" : "#000",
                backgroundColor:
                  selectedIntake === test.id ? "#F9EEFF" : "#fff",
                "&:hover": {
                  backgroundColor:
                    selectedIntake === test.id ? "#F9EEFF" : "#f5f5f5",
                },
                border:
                  selectedIntake === test.id ? "2px solid #572575" : "none",
                textTransform: "none",
                fontFamily: "inter",
                fontSize: "16px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                p: 2,
              }}
            >
              <img
                src={imageMap[test.intake] || "/SAPImages/exams/gmat.png"}
                alt={test.intake}
                style={{ width: "40px", height: "40px", objectFit: "contain" }}
              />

              <Typography
                sx={{
                  fontFamily: "inter",
                  fontWeight: selectedIntake === test.id ? 600 : 400,
                }}
              >
                {test.intake}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "inter",
                  fontWeight: selectedIntake === test.id ? 600 : 400,
                  fontSize: "14px",
                  color: selectedIntake === test.id ? "#572575" : "#000",
                }}
              >
                ({test.description})
              </Typography>
            </Button>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default forwardRef(StudySelectionForm);
